/**
 * useIntake Hook
 *
 * Drives one initiative intake session for the Plan surface: starting it,
 * answering clarifying questions, skipping the rest, and resuming an earlier
 * session. The transcript is read back from the intake's conversation after
 * every mutation, so the thread always reflects what the backend persisted
 * rather than a locally reconstructed guess.
 */

import {
  createSignal,
  createResource,
  createEffect,
  on,
  Accessor,
} from "solid-js";
import type {
  InitiativeIntake,
  IntakeId,
  Message,
  ProjectId,
  WorkItemId,
} from "../api/types";
import {
  getIntake,
  listIntakes,
  sendIntakeTurn,
  skipIntakeClarification,
  startIntake,
} from "../api/intake";
import { getConversation } from "../api/conversations";
import { describeAiError, surfaceQuotaExhausted } from "../utils/aiError";
import { activeIntakeId, setActiveIntakeId } from "../stores/planSessionStore";

export interface UseIntakeResult {
  /** The intake being worked on, if any */
  intake: Accessor<InitiativeIntake | undefined>;
  /** Persisted transcript for the current intake */
  messages: Accessor<Message[]>;
  /** Message awaiting a reply, shown optimistically while the AI works */
  pending: Accessor<string | null>;
  /** True while a request is in flight */
  busy: Accessor<boolean>;
  /** Human-readable failure from the last action, if it failed */
  error: Accessor<string | null>;
  /** Earlier intake sessions for this project, newest first */
  sessions: Accessor<InitiativeIntake[] | undefined>;
  /**
   * Describe an initiative and open a session. Resolves true on success.
   *
   * `targetEpicId` pins the session to an existing epic (FLOW-615) — the
   * breakdown then attaches its stories to that epic rather than proposing
   * one. Omit it for a session started from a blank composer.
   */
  start: (
    initiativeText: string,
    targetEpicId?: WorkItemId,
  ) => Promise<boolean>;
  /** Answer the current clarifying question. Resolves true on success. */
  answer: (content: string) => Promise<boolean>;
  /** Retire the remaining questions and assemble the spec */
  skip: () => Promise<void>;
  /** Reopen an earlier session */
  resume: (id: IntakeId) => Promise<void>;
  /** Clear the surface back to the empty state */
  reset: () => void;
  /** Dismiss the current error */
  clearError: () => void;
}

/**
 * @param shouldRestore When false, the project's in-progress session is not
 *   reopened on mount — the caller is opening the surface for something else
 *   (a seeded initiative, FLOW-614). The earlier session stays in the sessions
 *   list, so nothing is lost.
 */
export function useIntake(
  projectId: Accessor<ProjectId>,
  shouldRestore: Accessor<boolean> = () => true,
): UseIntakeResult {
  const [intake, setIntake] = createSignal<InitiativeIntake | undefined>();
  const [messages, setMessages] = createSignal<Message[]>([]);
  const [pending, setPending] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const [sessionsResource, { refetch: refetchSessions }] = createResource(
    projectId,
    listIntakes,
  );

  const clear = () => {
    setIntake(undefined);
    setMessages([]);
    setPending(null);
    setError(null);
  };

  const loadThread = async (intakeRow: InitiativeIntake) => {
    const conversation = await getConversation(intakeRow.conversation_id);
    setMessages(conversation.messages);
  };

  /** Run a mutation with the shared busy/error/optimistic-echo handling. */
  const run = async (
    echo: string | null,
    action: () => Promise<InitiativeIntake>,
  ): Promise<boolean> => {
    setBusy(true);
    setError(null);
    setPending(echo);
    try {
      const updated = await action();
      setIntake(updated);
      setActiveIntakeId(projectId(), updated.id);
      await loadThread(updated);
      // Keep the session list honest — a mutation can create a session or move
      // one from "In progress" to "Complete".
      refetchSessions();
      return true;
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      return false;
    } finally {
      setPending(null);
      setBusy(false);
    }
  };

  const start = (
    initiativeText: string,
    targetEpicId?: WorkItemId,
  ): Promise<boolean> =>
    run(initiativeText, () =>
      startIntake(projectId(), initiativeText, targetEpicId),
    );

  const answer = async (content: string): Promise<boolean> => {
    const current = intake();
    if (!current) return false;
    return run(content, async () => {
      const response = await sendIntakeTurn(current.id, content);
      return response.intake;
    });
  };

  const skip = async (): Promise<void> => {
    const current = intake();
    if (!current) return;
    await run(null, () => skipIntakeClarification(current.id));
  };

  const resume = async (id: IntakeId): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const row = await getIntake(id);
      setIntake(row);
      setActiveIntakeId(projectId(), row.id);
      await loadThread(row);
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      clear();
      setActiveIntakeId(projectId(), null);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setActiveIntakeId(projectId(), null);
    clear();
  };

  // Restore the project's in-progress session on mount and whenever the active
  // project changes, so leaving /plan and coming back does not lose the thread.
  createEffect(
    on(projectId, (id) => {
      clear();
      if (!shouldRestore()) {
        setActiveIntakeId(id, null);
        return;
      }
      const restored = activeIntakeId(id);
      if (restored) void resume(restored);
    }),
  );

  return {
    intake,
    messages,
    pending,
    busy,
    error,
    sessions: () => sessionsResource(),
    start,
    answer,
    skip,
    resume,
    reset,
    clearError: () => setError(null),
  };
}
